import React, { useState } from 'react';
import { ToastContainer } from '../components/Toast';
import ConfirmDialog from '../components/ConfirmDialog';
import AvatarDisplay from '../components/AvatarDisplay';
import { Icon } from '../components/mobile/MobileKit';

// Pages
import MapPage from '../pages/MapPage';
import MissionsPage from '../pages/MissionsPage';
import DeliveriesPage from '../pages/DeliveriesPage';
import DronesPage from '../pages/DronesPage';
import LogsPage from '../pages/LogsPage';
import ProfilePage from '../pages/ProfilePage';
import SettingsPage from '../pages/SettingsPage';
import AdminPage from '../pages/AdminPage';

import { SECTIONS } from '../config/constants';
import '../styles/MobileView.css';

const TABS = [
  { key: SECTIONS.MAP, icon: 'map', label: 'MAP' },
  { key: SECTIONS.MISSIONS, icon: 'assignment', label: 'MISSIONS' },
  { key: SECTIONS.DELIVERIES, icon: 'inventory_2', label: 'PARCELS' },
  { key: SECTIONS.DRONES, icon: 'helicopter', label: 'DRONES' },
  { key: SECTIONS.LOGS, icon: 'history', label: 'LOGS' },
];

/**
 * Mobile Layout — the Flutter app's shell: live map under a glass top bar,
 * tab bar at the bottom. Map tabs run full-bleed under both bars; every other
 * section is a page between them.
 */
function MobileLayout({
  activeSection,
  setActiveSection,
  user,
  logout,
  telemetry,
  dronesTelemetry,
  activeMissions,
  drones,
  reorderDrones,
  loadDrones,
  selectedDroneId,
  setSelectedDroneId,
  toasts,
  dismissToast,
  confirmDialog,
  handleConfirm,
}) {
  const isMap = activeSection === SECTIONS.MAP;
  // Map markers and the drone sheet hand over drone objects, pages hand over ids.
  const selectDrone = (d) => setSelectedDroneId(d && typeof d === 'object' ? (d.id || d.droneId || null) : (d || null));

  const page = (() => {
    switch (activeSection) {
      case SECTIONS.DELIVERIES:
        return (
          <DeliveriesPage
            drones={drones}
            dronesTelemetry={dronesTelemetry}
            selectedDroneId={selectedDroneId}
            onDroneSelect={setSelectedDroneId}
            onNavigate={setActiveSection}
            isMobileLayout
          />
        );
      case SECTIONS.DRONES:
        return (
          <DronesPage
            telemetry={telemetry}
            dronesTelemetry={dronesTelemetry}
            drones={drones}
            reorderDrones={reorderDrones}
            loadDrones={loadDrones}
            selectedDroneId={selectedDroneId}
            onDroneSelect={setSelectedDroneId}
            isMobileLayout
          />
        );
      case SECTIONS.LOGS:
        return <LogsPage telemetry={telemetry} dronesTelemetry={dronesTelemetry} selectedDroneId={selectedDroneId} />;
      case SECTIONS.PROFILE:
        return <ProfilePage isMobileLayout />;
      case SECTIONS.SETTINGS:
        return <SettingsPage isMobileLayout />;
      case SECTIONS.ADMIN:
        return <AdminPage isMobileLayout dronesTelemetry={dronesTelemetry} />;
      default:
        return null;
    }
  })();

  return (
    <div className="app mobile-shell">
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
      <ConfirmDialog
        show={confirmDialog.show}
        message={confirmDialog.message}
        onConfirm={() => handleConfirm(true)}
        onCancel={() => handleConfirm(false)}
      />

      {/* The map stays mounted so Leaflet keeps its view across tab switches. */}
      <div className="m-layer" style={{ display: isMap ? 'block' : 'none' }}>
        <MapPage
          telemetry={telemetry}
          dronesTelemetry={dronesTelemetry}
          activeMissions={activeMissions}
          drones={drones}
          sidebarVisible={false}
          selectedDroneId={selectedDroneId}
          onDroneSelect={selectDrone}
          isMobileLayout
          currentUserRole={user?.role || 'operator'}
          isActive={isMap}
        />
      </div>

      {activeSection === SECTIONS.MISSIONS && (
        <div className="m-layer">
          <MissionsPage
            telemetry={telemetry}
            dronesTelemetry={dronesTelemetry}
            activeMissions={activeMissions}
            drones={drones}
            selectedDroneId={selectedDroneId}
            onDroneSelect={setSelectedDroneId}
            isMobileLayout
          />
        </div>
      )}

      {page && <main className="m-page">{page}</main>}

      <MobileTopBar user={user} onLogout={logout} onNavigate={setActiveSection} />

      <nav className="m-nav" aria-label="Primary navigation">
        {TABS.map((tab) => {
          const active = activeSection === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              className={`m-b m-nav-item${active ? ' active' : ''}`}
              onClick={() => setActiveSection(tab.key)}
              aria-current={active ? 'page' : undefined}
            >
              <Icon name={tab.icon} size={24} />
              {tab.label}
            </button>
          );
        })}
      </nav>
    </div>
  );
}

function MobileTopBar({ user, onLogout, onNavigate }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const go = (section) => { setMenuOpen(false); onNavigate(section); };

  // The menu is a sibling, not a child: the bar's backdrop-filter would trap
  // its fixed backdrop inside the 64px bar.
  return (
    <>
      <header className="m-topbar">
        <button type="button" className="m-b m-brand" onClick={() => onNavigate(SECTIONS.MAP)}>
          <span className="m-logo"><img src={`${process.env.PUBLIC_URL}/beramlogo.jpg`} alt="" /></span>
          <span className="m-title">BeRam UTMS</span>
        </button>

        <button type="button" className="m-b m-avatar-btn" onClick={() => setMenuOpen((o) => !o)} aria-label="Account menu">
          <AvatarDisplay avatarId={user?.avatar} name={user?.name} size={32} profilePhoto={user?.profilePhoto} />
        </button>
      </header>

      {menuOpen && (
        <>
          <div className="m-menu-backdrop" onClick={() => setMenuOpen(false)} />
          <div className="m-menu" role="menu">
            <div className="m-menu-user">
              <b>{user?.name || 'Operator'}</b>
              <small>{user?.email}</small>
            </div>
            <button type="button" className="m-b m-menu-item" onClick={() => go(SECTIONS.PROFILE)}>
              <Icon name="person" size={18} />PROFILE
            </button>
            <button type="button" className="m-b m-menu-item" onClick={() => go(SECTIONS.SETTINGS)}>
              <Icon name="settings" size={18} />SETTINGS
            </button>
            {user?.role === 'admin' && (
              <button type="button" className="m-b m-menu-item" onClick={() => go(SECTIONS.ADMIN)}>
                <Icon name="admin_panel_settings" size={18} />ADMIN PANEL
              </button>
            )}
            <div className="m-menu-sep" />
            <button type="button" className="m-b m-menu-item danger" onClick={() => { setMenuOpen(false); onLogout(); }}>
              <Icon name="logout" size={18} />LOGOUT
            </button>
          </div>
        </>
      )}
    </>
  );
}

export default MobileLayout;
